import classNames from "classnames";
import { FC, memo } from "react";
import { Link } from "react-router-dom";

import Tag, { TagTone } from "modules/assets/Tag";

import styles from "./style.module.scss";

type GaugeTone = "orange" | "red" | "green";

export type ThermalCardData = {
  title: string;
  unit: string;
  sensor?: { id: string; location: string };
  status: { tone: TagTone; label: string };
  realtimeLabel: string;
  realtimeValue: string;
  realtimeUnit: string;
  gauge?: {
    min: number;
    max: number;
    rangeLow: number;
    rangeHigh: number;
    value: number;
    tone: GaugeTone;
  };
  rangeCaption: string;
  alert?: {
    tone: GaugeTone;
    title: string;
    value: string;
    lines: string[];
    emphasis?: string;
  };
  footerNote?: string;
  updatedNote: string;
  outdatedNote?: string;
};

type PropTypes = {
  data: ThermalCardData;
  to?: string;
  className?: string;
};

const toPercent = (value: number, min: number, max: number) =>
  Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

const ThermalCard: FC<PropTypes> = ({ data, to, className }) => {
  const { gauge } = data;

  return (
    <article className={classNames(styles.card, className)}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.title}>
            <span className={styles.titleText}>{data.title}</span>
            <span className={styles.unit}>{data.unit}</span>
          </div>

          {data.sensor && (
            <div className={styles.sensorMeta}>
              <span className={styles.name}>Sensor: {data.sensor.id}</span>
              <span className={styles.name}>Location: {data.sensor.location}</span>
            </div>
          )}
        </div>

        <Tag tone={data.status.tone}>{data.status.label}</Tag>
      </header>

      <div className={styles.realtime}>
        <span className={styles.realtimeLabel}>{data.realtimeLabel}</span>

        <div className={styles.realtimeValue}>
          <span className={styles.realtimeNumber}>{data.realtimeValue}</span>
          <span className={styles.realtimeUnit}>{data.realtimeUnit}</span>
        </div>
      </div>

      {gauge && (
        <div className={styles.gauge}>
          <div className={classNames(styles.track, styles[`track-${gauge.tone}`])}>
            <span
              className={classNames(styles.band, styles[`band-${gauge.tone}`])}
              style={{
                left: `${toPercent(gauge.rangeLow, gauge.min, gauge.max)}%`,
                width: `${toPercent(gauge.rangeHigh, gauge.min, gauge.max) - toPercent(gauge.rangeLow, gauge.min, gauge.max)}%`,
              }}
            />
            <span className={styles.tick} style={{ left: `${toPercent(gauge.rangeLow, gauge.min, gauge.max)}%` }} />
            <span className={styles.tick} style={{ left: `${toPercent(gauge.rangeHigh, gauge.min, gauge.max)}%` }} />
            <span
              className={classNames(styles.thumb, styles[`thumb-${gauge.tone}`])}
              style={{ left: `${toPercent(gauge.value, gauge.min, gauge.max)}%` }}
            />
          </div>

          <div className={styles.scale}>
            <span
              className={classNames(styles.scaleLabel, styles[`scaleLabel-${gauge.tone}`])}
              style={{ left: `${toPercent(gauge.rangeLow, gauge.min, gauge.max)}%` }}
            >
              {gauge.rangeLow}
            </span>
            <span
              className={classNames(styles.scaleLabel, styles[`scaleLabel-${gauge.tone}`])}
              style={{ left: `${toPercent(gauge.rangeHigh, gauge.min, gauge.max)}%` }}
            >
              {gauge.rangeHigh}
            </span>
          </div>
        </div>
      )}

      <span className={styles.rangeCaption}>{data.rangeCaption}</span>

      {data.alert && (
        <div className={classNames(styles.alert, styles[`alert-${data.alert.tone}`])}>
          <div className={styles.alertHead}>
            <span className={styles.alertTitle}>{data.alert.title}</span>
            <span className={styles.alertValue}>{data.alert.value}</span>
          </div>

          {data.alert.lines.map((line, index) => (
            <span key={line} className={classNames(styles.alertLine, index === 0 && styles.alertLinePrimary)}>
              {line}
            </span>
          ))}

          {data.alert.emphasis && <span className={styles.alertEmphasis}>{data.alert.emphasis}</span>}
        </div>
      )}

      <footer className={styles.footer}>
        {data.footerNote &&
          (to ? (
            <Link to={to} className={styles.footerNote}>
              {data.footerNote}
            </Link>
          ) : (
            <span className={styles.footerNote}>{data.footerNote}</span>
          ))}
        <span className={styles.footerUpdated}>
          {data.updatedNote}
          {data.outdatedNote && <span className={styles.footerOutdated}>{data.outdatedNote}</span>}
        </span>
      </footer>
    </article>
  );
};

export default memo(ThermalCard);
